import { useMemo, useState } from "react";
import { FileUp, ListFilter, Map, Plus, Store } from "lucide-react";
import { ImportShops } from "../features/imports/components/ImportShops";
import { ShopsMap } from "../features/map/components/ShopsMap";
import { FilterBar } from "../features/shops/components/FilterBar";
import { ShopDetailPanel } from "../features/shops/components/ShopDetailPanel";
import { ShopForm } from "../features/shops/components/ShopForm";
import { ShopList } from "../features/shops/components/ShopList";
import { EMPTY_FILTERS } from "../features/shops/constants";
import { useShops } from "../features/shops/hooks/useShops";
import type { Shop, ShopCreateInput } from "../features/shops/types";
import { filterAndSortShops, hasExactLocation } from "../features/shops/utils";

type AppTab = "entry" | "list" | "map" | "import";

const tabs: Array<{ id: AppTab; label: string; icon: typeof Plus }> = [
  { id: "entry", label: "Add shop", icon: Plus },
  { id: "list", label: "Shops list", icon: ListFilter },
  { id: "map", label: "Map view", icon: Map },
  { id: "import", label: "Import", icon: FileUp }
];

export function App() {
  const {
    shops,
    importBatches,
    isLoading,
    error,
    createShop,
    updateShop,
    deleteShop,
    importShops
  } = useShops();

  const [activeTab, setActiveTab] = useState<AppTab>("entry");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);

  const filteredShops = useMemo(() => filterAndSortShops(shops, filters), [filters, shops]);
  const selectedShop = shops.find((shop) => shop.id === selectedShopId) ?? null;
  const locatedCount = shops.filter((shop) => hasExactLocation(shop.shopLocation)).length;
  const importedCount = shops.filter((shop) => shop.source === "import").length;
  const missingLocationCount = shops.filter((shop) => shop.shopLocation.status !== "exact").length;

  const handleSelectShop = (shop: Shop) => {
    setSelectedShopId(shop.id);
  };

  const handleEditShop = (shop: Shop) => {
    setEditingShop(shop);
    setSelectedShopId(shop.id);
    setActiveTab("entry");
  };

  const handleDeleteShop = async (shop: Shop) => {
    const confirmed = window.confirm(`Delete ${shop.shopName}?`);

    if (!confirmed) {
      return;
    }

    await deleteShop(shop.id);

    if (selectedShopId === shop.id) {
      setSelectedShopId(null);
    }

    if (editingShop?.id === shop.id) {
      setEditingShop(null);
    }
  };

  const handleSaveShop = async (input: ShopCreateInput, shopBeingEdited: Shop | null) => {
    if (shopBeingEdited) {
      await updateShop(shopBeingEdited.id, {
        ...input,
        source: shopBeingEdited.source,
        importBatchId: shopBeingEdited.importBatchId
      });
      setSelectedShopId(shopBeingEdited.id);
      setEditingShop(null);
      return;
    }

    const createdShop = await createShop(input);
    setSelectedShopId(createdShop.id);
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-block">
          <div className="brand-icon" aria-hidden="true">
            <Store size={22} />
          </div>
          <div>
            <p className="eyebrow">Marmara Wholesale</p>
            <h1>Cosmetic shop field app</h1>
          </div>
        </div>

        <div className="header-stats" aria-label="Shop summary">
          <span>
            <strong>{shops.length}</strong>
            shops
          </span>
          <span>
            <strong>{locatedCount}</strong>
            mapped
          </span>
          <span>
            <strong>{importedCount}</strong>
            imported
          </span>
          <span>
            <strong>{missingLocationCount}</strong>
            missing location
          </span>
        </div>
      </header>

      <nav className="tab-nav" aria-label="Application sections">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              type="button"
              key={tab.id}
              className={isActive ? "active" : ""}
              onClick={() => setActiveTab(tab.id)}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {error ? <div className="alert error-alert">{error}</div> : null}

      <main>
        {activeTab === "entry" ? (
          <ShopForm editingShop={editingShop} onSubmit={handleSaveShop} onCancelEdit={() => setEditingShop(null)} />
        ) : null}

        {activeTab === "list" ? (
          <div className="screen-stack">
            <FilterBar filters={filters} shops={shops} onChange={setFilters} />
            <div className="workbench-layout">
              <ShopList
                shops={filteredShops}
                isLoading={isLoading}
                selectedShopId={selectedShopId}
                onSelectShop={handleSelectShop}
                onEditShop={handleEditShop}
                onDeleteShop={handleDeleteShop}
              />
              <ShopDetailPanel shop={selectedShop} onEditShop={handleEditShop} onDeleteShop={handleDeleteShop} />
            </div>
          </div>
        ) : null}

        {activeTab === "map" ? (
          <div className="screen-stack">
            <FilterBar filters={filters} shops={shops} onChange={setFilters} />
            <div className="map-layout">
              <ShopsMap shops={filteredShops} selectedShopId={selectedShopId} onSelectShop={handleSelectShop} />
              <ShopDetailPanel shop={selectedShop} onEditShop={handleEditShop} onDeleteShop={handleDeleteShop} />
            </div>
          </div>
        ) : null}

        {activeTab === "import" ? (
          <div className="import-layout">
            <ImportShops existingShops={shops} onImportShops={importShops} />

            <aside className="import-history">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">History</p>
                  <h2>Import batches</h2>
                </div>
              </div>
              {importBatches.length === 0 ? (
                <p className="muted-note">No imports yet.</p>
              ) : (
                <ul>
                  {importBatches.map((batch) => (
                    <li key={batch.id}>
                      <strong>{batch.fileName}</strong>
                      <span>
                        {batch.importedRows} imported · {batch.rejectedRows} rejected
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </aside>
          </div>
        ) : null}
      </main>
    </div>
  );
}
